// src/components/messaging/NewConversationDialog.tsx
import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  CircularProgress,
  Box,
  InputAdornment,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Divider,
  alpha,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import InboxIcon from '@mui/icons-material/Inbox';
import ChatIcon from '@mui/icons-material/Chat';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import { useMessaging } from '../../contexts/MessagingContext';
import { useTranslation } from 'react-i18next';
import { MessagingUser, Contact } from '../../types/messaging';

interface NewConversationDialogProps {
  open: boolean;
  onClose: () => void;
}

type TabType = 'contacts' | 'search' | 'requests';

const NewConversationDialog: React.FC<NewConversationDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const {
    state,
    searchUsers,
    createConversation,
    fetchContacts,
    fetchContactRequests,
    sendContactRequest,
    acceptContactRequest,
    rejectContactRequest,
  } = useMessaging();
  const { contacts, contactRequests, searchResults } = state;

  const [activeTab, setActiveTab] = useState<TabType>('contacts');
  const [query, setQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [processingUsers, setProcessingUsers] = useState<Set<number>>(new Set());

  // Fetch contacts and requests on open
  useEffect(() => {
    if (open) {
      fetchContacts();
      fetchContactRequests();
      setQuery('');
      setActiveTab('contacts');
      setProcessingUsers(new Set());
    }
  }, [open, fetchContacts, fetchContactRequests]);

  // Debounced search
  useEffect(() => {
    if (activeTab !== 'search') return;

    const timeoutId = setTimeout(async () => {
      if (query.trim()) {
        setIsSearching(true);
        await searchUsers(query.trim());
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, searchUsers, activeTab]);

  // Handle starting a conversation with a contact
  const handleStartConversation = async (user: MessagingUser) => {
    setIsCreating(true);
    try {
      await createConversation(user.id);
      onClose();
    } catch (error) {
      console.error('Failed to create conversation:', error);
    } finally {
      setIsCreating(false);
    }
  };

  // Handle sending a contact request
  const handleSendRequest = async (userId: number) => {
    try {
      setProcessingUsers((prev) => new Set(Array.from(prev).concat(userId)));
      await sendContactRequest(userId);
      // Refresh search results to get updated status
      if (query.trim()) {
        await searchUsers(query.trim());
      }
    } catch (error) {
      console.error('Failed to send contact request:', error);
    } finally {
      setProcessingUsers((prev) => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  // Handle accepting a contact request
  const handleAcceptRequest = async (requestId: number, userId: number) => {
    try {
      setProcessingUsers((prev) => new Set(Array.from(prev).concat(userId)));
      await acceptContactRequest(requestId);
      await fetchContacts();
      await fetchContactRequests();
    } catch (error) {
      console.error('Failed to accept request:', error);
    } finally {
      setProcessingUsers((prev) => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  // Handle rejecting a contact request
  const handleRejectRequest = async (requestId: number, userId: number) => {
    try {
      setProcessingUsers((prev) => new Set(Array.from(prev).concat(userId)));
      await rejectContactRequest(requestId);
      await fetchContactRequests();
    } catch (error) {
      console.error('Failed to reject request:', error);
    } finally {
      setProcessingUsers((prev) => {
        const newSet = new Set(Array.from(prev));
        newSet.delete(userId);
        return newSet;
      });
    }
  };

  // Get initials
  const getInitials = (name?: string): string => {
    if (!name) return '?';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return name.slice(0, 2).toUpperCase();
  };

  // Extract users from contacts
  const contactUsers = (Array.isArray(contacts) ? contacts : [])
    .map((c: Contact) => c.user || c.contact)
    .filter((u): u is MessagingUser => u !== undefined && u !== null);

  // Get pending contact requests (received)
  const receivedRequests = (Array.isArray(contactRequests) ? contactRequests : []).filter(
    (r) => r.status === 'pending' && (r.sender || r.from_user)
  );

  // Render action button based on contact_status
  const renderActionButton = (user: MessagingUser) => {
    const isProcessing = processingUsers.has(user.id);
    const status = user.contact_status;

    if (isProcessing) {
      return <CircularProgress size={24} />;
    }

    // Status: accepted → Message button
    if (status === 'accepted') {
      return (
        <Button
          size="small"
          variant="contained"
          color="primary"
          startIcon={<ChatIcon />}
          onClick={(e) => {
            e.stopPropagation();
            handleStartConversation(user);
          }}
          disabled={isCreating}
        >
          {t('messaging.message', 'Message')}
        </Button>
      );
    }

    // Status: pending_sent → Waiting chip
    if (status === 'pending_sent') {
      return (
        <Chip
          icon={<HourglassEmptyIcon />}
          label={t('messaging.waiting', 'En attente')}
          size="small"
          color="warning"
          variant="outlined"
        />
      );
    }

    // Status: pending_received → Accept/Reject buttons
    if (status === 'pending_received') {
      return (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <IconButton
            color="success"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              // Find the request ID for this user
              const request = receivedRequests.find(
                (r) => (r.sender?.id || r.from_user?.id) === user.id
              );
              if (request) {
                handleAcceptRequest(request.id, user.id);
              }
            }}
            title={t('messaging.accept', 'Accepter')}
          >
            <CheckIcon />
          </IconButton>
          <IconButton
            color="error"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              const request = receivedRequests.find(
                (r) => (r.sender?.id || r.from_user?.id) === user.id
              );
              if (request) {
                handleRejectRequest(request.id, user.id);
              }
            }}
            title={t('messaging.reject', 'Refuser')}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      );
    }

    // Status: null or undefined → Add button
    return (
      <Button
        size="small"
        variant="outlined"
        startIcon={<PersonAddIcon />}
        onClick={(e) => {
          e.stopPropagation();
          handleSendRequest(user.id);
        }}
      >
        {t('messaging.addContact', 'Ajouter')}
      </Button>
    );
  };

  // Render user list item for search
  const renderSearchUserItem = (user: MessagingUser) => {
    return (
      <ListItemButton
        key={user.id}
        onClick={() => user.contact_status === 'accepted' && handleStartConversation(user)}
        disabled={isCreating}
        sx={{
          borderRadius: 2,
          mb: 0.5,
          '&:hover': {
            backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        <ListItemAvatar>
          <Avatar src={user.avatar_url || undefined}>{getInitials(user.name)}</Avatar>
        </ListItemAvatar>
        <ListItemText
          primary={user.name}
          secondary={user.email}
          primaryTypographyProps={{ fontWeight: 500 }}
        />
        {renderActionButton(user)}
      </ListItemButton>
    );
  };

  // Render contacts tab
  const renderContactsTab = () => {
    if (contactUsers.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 4,
            color: 'text.secondary',
          }}
        >
          <PersonIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
          <Typography variant="body2">
            {t('messaging.noContacts', 'Aucun contact')}
          </Typography>
          <Typography variant="caption" sx={{ mt: 1 }}>
            {t('messaging.searchToAddContacts', 'Utilisez la recherche pour ajouter des contacts')}
          </Typography>
        </Box>
      );
    }

    return (
      <List sx={{ maxHeight: 300, overflow: 'auto' }}>
        {contactUsers.map((user) => (
          <ListItemButton
            key={user.id}
            onClick={() => handleStartConversation(user)}
            disabled={isCreating}
            sx={{
              borderRadius: 2,
              mb: 0.5,
              '&:hover': {
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <ListItemAvatar>
              <Avatar src={user.avatar_url || undefined}>{getInitials(user.name)}</Avatar>
            </ListItemAvatar>
            <ListItemText
              primary={user.name}
              secondary={user.email}
              primaryTypographyProps={{ fontWeight: 500 }}
            />
            <ChatIcon color="primary" />
          </ListItemButton>
        ))}
      </List>
    );
  };

  // Render search tab
  const renderSearchTab = () => {
    const results = Array.isArray(searchResults) ? searchResults : [];

    return (
      <>
        <TextField
          fullWidth
          placeholder={t('messaging.searchUsers', 'Rechercher un utilisateur...')}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                {isSearching ? <CircularProgress size={20} /> : <SearchIcon />}
              </InputAdornment>
            ),
          }}
        />

        {!query.trim() ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
              color: 'text.secondary',
            }}
          >
            <SearchIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2">
              {t('messaging.typeToSearch', 'Tapez pour rechercher des utilisateurs')}
            </Typography>
          </Box>
        ) : results.length === 0 && !isSearching ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 4,
              color: 'text.secondary',
            }}
          >
            <PersonIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2">
              {t('messaging.noUsersFound', 'Aucun utilisateur trouvé')}
            </Typography>
          </Box>
        ) : (
          <List sx={{ maxHeight: 250, overflow: 'auto' }}>
            {results.map((user) => renderSearchUserItem(user))}
          </List>
        )}
      </>
    );
  };

  // Render requests tab
  const renderRequestsTab = () => {
    if (receivedRequests.length === 0) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            py: 4,
            color: 'text.secondary',
          }}
        >
          <InboxIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
          <Typography variant="body2">
            {t('messaging.noRequests', 'Aucune demande de contact')}
          </Typography>
        </Box>
      );
    }

    return (
      <List sx={{ maxHeight: 300, overflow: 'auto' }}>
        {receivedRequests.map((request) => {
          const fromUser = request.sender || request.from_user;
          if (!fromUser) return null;

          const isProcessing = processingUsers.has(fromUser.id);

          return (
            <ListItemButton
              key={request.id}
              disabled={isProcessing}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                cursor: 'default',
                '&:hover': {
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
                },
              }}
            >
              <ListItemAvatar>
                <Avatar src={fromUser.avatar_url || undefined}>
                  {getInitials(fromUser.name)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={fromUser.name}
                secondary={fromUser.email}
                primaryTypographyProps={{ fontWeight: 500 }}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                {isProcessing ? (
                  <CircularProgress size={24} />
                ) : (
                  <>
                    <IconButton
                      color="success"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleAcceptRequest(request.id, fromUser.id);
                      }}
                      title={t('messaging.accept', 'Accepter')}
                    >
                      <CheckIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRejectRequest(request.id, fromUser.id);
                      }}
                      title={t('messaging.reject', 'Refuser')}
                    >
                      <CloseIcon />
                    </IconButton>
                  </>
                )}
              </Box>
            </ListItemButton>
          );
        })}
      </List>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('messaging.newConversation', 'Nouvelle conversation')}</DialogTitle>
      <DialogContent>
        <Tabs
          value={activeTab}
          onChange={(_, value) => setActiveTab(value as TabType)}
          sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab
            label={t('messaging.contacts', 'Contacts')}
            value="contacts"
          />
          <Tab
            label={t('messaging.search', 'Recherche')}
            value="search"
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {t('messaging.requests', 'Demandes')}
                {receivedRequests.length > 0 && (
                  <Chip
                    label={receivedRequests.length}
                    size="small"
                    color="error"
                    sx={{ height: 20, minWidth: 20 }}
                  />
                )}
              </Box>
            }
            value="requests"
          />
        </Tabs>

        <Divider sx={{ mb: 2 }} />

        {activeTab === 'contacts' && renderContactsTab()}
        {activeTab === 'search' && renderSearchTab()}
        {activeTab === 'requests' && renderRequestsTab()}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isCreating}>
          {t('common.cancel', 'Annuler')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default NewConversationDialog;
